import { Link } from "react-router-dom";

import type { APISurvey } from "~/api/apiTypes";
import { useSurveysList } from "~/features/surveys/hooks";
import { isSome } from "~/utils";

function StateBadge({ state }: { state: APISurvey["state"] }) {
  const isFinished = state === "finished";
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
        isFinished
          ? "bg-green-100 text-green-800"
          : "bg-yellow-100 text-yellow-800"
      }`}
    >
      {isFinished ? "Dokonceno" : "Probíhá"}
    </span>
  );
}

export function SurveyListPage() {
  const { data, isPending, isError } = useSurveysList();

  if (isPending) return <p className="p-6 text-slate-500">Načítání...</p>;
  if (isError || !isSome(data))
    return <p className="p-6 text-red-600">Chyba při načítání dat.</p>;

  return (
    <div>
      <h1
        id="survey-list-heading"
        className="text-xl font-semibold text-slate-800"
      >
        Průzkumy
      </h1>
      {data.length === 0 ? (
        <p className="mt-4 text-slate-500">Žádné průzkumy k zobrazení.</p>
      ) : (
        <div
          role="region"
          aria-labelledby="survey-list-heading"
          tabIndex={0}
          className="mt-4 max-w-full overflow-x-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th scope="col" className="pb-2 pr-4 font-medium">
                  Projekt
                </th>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  Lokalita / Zákazník
                </th>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  Stav
                </th>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  Fotografie
                </th>
                <th scope="col" className="pb-2 font-medium">
                  Datum
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.surveyId} className="border-b hover:bg-slate-50">
                  <td className="py-2 pr-4">
                    <Link
                      to={`/surveys/${item.surveyId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {item.projectName}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 text-slate-600">
                    {item.location ?? item.client ?? "–"}
                  </td>
                  <td className="py-2 pr-4">
                    <StateBadge state={item.state} />
                  </td>
                  <td className="py-2 pr-4 text-slate-600">
                    {item.imagesCount}
                  </td>
                  <td className="py-2 text-slate-500">
                    {item.finishedAt
                      ? new Date(item.finishedAt).toLocaleDateString("cs-CZ")
                      : new Date(item.createdAt).toLocaleDateString("cs-CZ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
